/**
 * Mobile-optimierte Tagesansicht der Einsatzplanung.
 * Statt der breiten Wochen-Tabelle: Tagesauswahl + gestapelte, volle-Breite-Karten
 * pro Maßnahme mit Name, Zeit, Raum und Aktivitäten. Nur < lg sichtbar.
 */
import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  User, Calendar, Clock, DoorOpen, Coffee, Repeat, AlertTriangle, Trash2, Plus, Users,
} from 'lucide-react';
import { ACTIVITY_TYPES } from './index';

const initials = (name) => (name || '').split(' ').reverse().map((n) => n[0]).filter(Boolean).join('').slice(0, 3);

export default function MobileShiftView({
  weekDays,
  filteredMassnahmen,
  getShiftsForMassnahmeAndDay,
  getActivitiesForMassnahmeAndDay,
  getEmployeeConflicts,
  getAbsenceForShift,
  canManage,
  openNewShiftDialog,
  openEditShiftDialog,
  openActivityDialog,
  openEditActivityDialog,
  handleDeleteActivity,
  holidayMap = {},
}) {
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const defaultIdx = Math.max(0, weekDays.findIndex((d) => format(d, 'yyyy-MM-dd') === todayKey));
  const [dayIdx, setDayIdx] = useState(defaultIdx);
  const day = weekDays[dayIdx] || weekDays[0];
  const dayKey = day ? format(day, 'yyyy-MM-dd') : '';
  const holiday = holidayMap[dayKey];

  // Maßnahmen mit Einträgen an diesem Tag
  const sections = useMemo(() => {
    if (!day) return [];
    return filteredMassnahmen
      .map((m) => {
        const shifts = getShiftsForMassnahmeAndDay(m.id, day) || [];
        const activities = getActivitiesForMassnahmeAndDay(m.id, day) || [];
        return { m, shifts, activities, count: shifts.length + activities.length };
      })
      .filter((s) => s.count > 0);
  }, [filteredMassnahmen, day, getShiftsForMassnahmeAndDay, getActivitiesForMassnahmeAndDay]);

  if (!day) return null;

  return (
    <div className="p-3 space-y-4" data-testid="mobile-shift-view">
      {/* Tagesauswahl */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {weekDays.map((d, i) => {
          const k = format(d, 'yyyy-MM-dd');
          const isSel = i === dayIdx;
          const isToday = k === todayKey;
          const hol = holidayMap[k];
          return (
            <button
              key={k}
              onClick={() => setDayIdx(i)}
              data-testid={`mobile-day-${k}`}
              className={`flex-shrink-0 min-w-[64px] rounded-xl px-3 py-2 text-center border transition-colors ${
                isSel ? 'bg-primary text-primary-foreground border-primary'
                      : hol ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-950/20'
                            : 'bg-card border-border/60 hover:bg-muted/50'
              }`}
            >
              <div className="text-[11px] uppercase opacity-80">{format(d, 'EE', { locale: de })}</div>
              <div className={`text-base font-bold leading-tight ${!isSel && isToday ? 'text-primary' : ''}`}>{format(d, 'dd.MM.', { locale: de })}</div>
            </button>
          );
        })}
      </div>

      {holiday && (
        <div className="text-xs px-3 py-2 rounded-lg bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-200 flex items-center gap-2" data-testid="mobile-holiday">
          <span aria-hidden="true">★</span>
          {holiday.is_closure ? 'Schließtag' : 'Feiertag'}: {holiday.name}
        </div>
      )}

      {sections.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-10 border border-dashed rounded-xl">
          Keine Einsätze oder Aktivitäten an diesem Tag.
        </div>
      ) : (
        sections.map(({ m, shifts, activities }) => (
          <Card key={m.id} className="overflow-hidden" data-testid={`mobile-massnahme-${m.id}`}>
            <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderLeft: `4px solid ${m.color}` }}>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: m.color }}>
                {(m.short_code || '').slice(0, 3)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm leading-tight truncate">{m.name}</p>
                <p className="text-[11px] text-muted-foreground truncate">{m.short_code}</p>
              </div>
            </div>

            <div className="p-2 space-y-2">
              {activities.map((a) => {
                const actType = ACTIVITY_TYPES.find((t) => t.value === a.activity_type);
                return (
                  <div
                    key={a.id}
                    onClick={() => canManage && openEditActivityDialog && openEditActivityDialog(a)}
                    className={`rounded-lg border bg-gradient-to-r from-gray-50 to-white dark:from-muted/40 dark:to-transparent p-3 ${canManage ? 'active:scale-[0.99] cursor-pointer' : ''}`}
                    data-testid={`mobile-activity-${a.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: actType?.color || '#6B7280' }} />
                      <span className="font-medium text-sm flex-1">{a.activity_label}</span>
                      {canManage && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteActivity(a.id); }}
                          className="text-red-500 p-1"
                          title="Aktivität löschen"
                        ><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1 pl-4">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{(a.start_time || '').slice(0, 5) || 'Ganztags'}{a.end_time ? `–${(a.end_time || '').slice(0, 5)}` : ''}</span>
                      {a.participant_count ? <span className="flex items-center gap-1"><Users className="h-3 w-3" />{a.participant_count} TN</span> : null}
                    </div>
                  </div>
                );
              })}

              {shifts.map((s) => {
                const conflicts = getEmployeeConflicts ? getEmployeeConflicts(s, day) : [];
                const hasConflict = conflicts && conflicts.length > 1;
                const absence = getAbsenceForShift ? getAbsenceForShift(s.employee_id, dayKey) : null;
                let statusClass = '';
                if (absence && absence.status === 'approved') statusClass = 'ring-2 ring-red-500';
                else if (absence && absence.status === 'pending') statusClass = 'outline outline-2 outline-dashed outline-red-400';
                else if (hasConflict) statusClass = 'ring-2 ring-amber-400';
                return (
                  <div
                    key={s.id}
                    onClick={() => canManage && openEditShiftDialog(s)}
                    title={absence ? absence.tooltip : undefined}
                    data-testid={`mobile-shift-${s.id}`}
                    className={`rounded-lg border bg-card shadow-sm p-3 ${statusClass} ${canManage ? 'active:scale-[0.99] cursor-pointer' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0" style={{ backgroundColor: m.color }}>
                        {initials(s.employee_name)}
                      </div>
                      <span className="font-semibold text-sm flex-1">{s.employee_name}</span>
                      {s.series_id && <Repeat className="h-3.5 w-3.5 text-primary/70 flex-shrink-0" title="Teil einer Serie" />}
                      {hasConflict && <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground mt-1.5 pl-9">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.start_time && s.end_time ? `${(s.start_time || '').slice(0, 5)}\u2013${(s.end_time || '').slice(0, 5)}` : 'Ganztags'}</span>
                      {(() => {
                        const dmap = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                        const wd = s.date ? dmap[new Date(`${s.date}T00:00:00`).getDay()] : null;
                        const wp = m.weekday_pauses;
                        const p = (wp && typeof wp === 'object' && wd && wp[wd] != null && wp[wd] !== '')
                          ? Number(wp[wd])
                          : (m.pause_minutes != null && m.pause_minutes !== '' ? Number(m.pause_minutes) : 0);
                        return p > 0 ? <span className="flex items-center gap-1"><Coffee className="h-3 w-3" />{p} Min</span> : null;
                      })()}
                      {s.room_name && <span className="flex items-center gap-1"><DoorOpen className="h-3 w-3" />{s.room_name}</span>}
                      {absence && <span className="text-red-600 font-medium">{absence.status === 'approved' ? 'Abwesend' : 'Urlaub beantragt'}</span>}
                    </div>
                  </div>
                );
              })}

              {canManage && (
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm" className="flex-1 h-8" onClick={() => openNewShiftDialog(day, m.id)} data-testid={`mobile-add-shift-${m.id}`}>
                    <User className="h-4 w-4 mr-1" /> Einsatz
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 h-8" onClick={() => openActivityDialog(day, m.id)} data-testid={`mobile-add-activity-${m.id}`}>
                    <Calendar className="h-4 w-4 mr-1" /> Aktivität
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))
      )}

      {canManage && (
        <p className="text-[11px] text-muted-foreground text-center pt-1">
          Tipp: Neue Einträge für leere Maßnahmen über „Neuer Einsatz" oben anlegen.
        </p>
      )}
    </div>
  );
}
